import { clampTier, midiToAbc, midiToNoteName, type ClefPreference } from '@/lib/musicTheory'

export type Clef = 'treble' | 'bass'

export interface GeneratedNote {
  midi: number
  clef: Clef
  /** Letter the kid types: A–G. */
  letter: string
  /** Letter plus octave, e.g. "G4" — recorded on the attempt, not shown as the answer. */
  fullName: string
  abc: string
}

/**
 * Naturals only — the answer is a single letter, so accidentals have no place here.
 * Difficulty widens the reading range instead: each step adds notes outward from
 * the five the kid starts with.
 */
const TREBLE_STEPS: number[][] = [
  [60, 62, 64, 65, 67], // C4 D4 E4 F4 G4 — middle C up to the second line
  [69, 71, 72],         // A4 B4 C5 — the rest of the staff's lower half
  [57, 59, 74, 76, 77], // A3 B3 below, D5 E5 F5 up to the top line
  [53, 55, 79, 81],     // F3 G3, G5 A5 — one ledger line each way
  [50, 52, 83, 84],     // D3 E3, B5 C6 — two ledger lines each way
]

const BASS_STEPS: number[][] = [
  [48, 50, 52, 53, 55], // C3 D3 E3 F3 G3 — the staff's upper half
  [57, 59, 60],         // A3 B3 C4 — up to middle C on its ledger line
  [45, 47, 62, 64, 65], // A2 B2 below, D4 E4 F4 above
  [41, 43, 67, 69],     // F2 G2 down to the bottom line, G4 A4 above
  [38, 40, 71, 72],     // D2 E2, B4 C5 — ledger lines both ways
]

/** Tier at which a BOTH-clef child is handed the second clef. */
const SECOND_CLEF_TIER = 4

export function noteTierForLevel(difficultyLevel: number): number {
  return clampTier(difficultyLevel)
}

function stepsUpTo(steps: number[][], count: number): number[] {
  return steps.slice(0, Math.max(0, Math.min(steps.length, count))).flat()
}

function stepsFor(clef: Clef): number[][] {
  return clef === 'bass' ? BASS_STEPS : TREBLE_STEPS
}

/** The notes in play, grouped so each clef gets an even share of the questions. */
interface NotePool {
  byClef: { clef: Clef; midis: number[] }[]
}

/**
 * A single-clef preference spends every step on that clef. BOTH keeps climbing the
 * primary clef the whole way and *adds* the second one at tier 4, starting from its
 * own easiest range — so the ledger lines at the top of the primary ladder stay
 * reachable instead of being traded away for the new clef.
 */
function notePoolForTier(tier: number, clefPreference: ClefPreference): NotePool {
  const primary: Clef = clefPreference === 'BASS' ? 'bass' : 'treble'
  const primaryGroup = { clef: primary, midis: stepsUpTo(stepsFor(primary), tier) }

  if (clefPreference !== 'BOTH') {
    return { byClef: [primaryGroup] }
  }

  const secondary: Clef = primary === 'treble' ? 'bass' : 'treble'
  const secondaryMidis = stepsUpTo(stepsFor(secondary), tier - (SECOND_CLEF_TIER - 1))
  return {
    byClef: secondaryMidis.length > 0
      ? [primaryGroup, { clef: secondary, midis: secondaryMidis }]
      : [primaryGroup],
  }
}

const LETTERS_BY_PITCH_CLASS: Record<number, string> = {
  0: 'C', 2: 'D', 4: 'E', 5: 'F', 7: 'G', 9: 'A', 11: 'B',
}

export const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'] as const

function letterForMidi(midi: number): string {
  return LETTERS_BY_PITCH_CLASS[midi % 12] ?? 'C'
}

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

function toNote(midi: number, clef: Clef): GeneratedNote {
  return {
    midi,
    clef,
    letter: letterForMidi(midi),
    fullName: midiToNoteName(midi),
    abc: `X:1\nK:C clef=${clef}\nL:1/1\n${midiToAbc(midi, clef)}|\n`,
  }
}

/**
 * Picks the next note. Clefs are drawn evenly rather than by pool size, so a newly
 * unlocked clef gets real practice instead of the handful of questions its smaller
 * range would earn. An immediate repeat of the previous note is avoided so the kid
 * can't coast on the last answer.
 */
function generateNote(pool: NotePool, previous?: GeneratedNote | null): GeneratedNote {
  const groups = pool.byClef.filter((g) => g.midis.length > 0)
  if (groups.length === 0) return toNote(60, 'treble')

  const group = pick(groups)
  const candidates =
    previous && previous.clef === group.clef && group.midis.length > 1
      ? group.midis.filter((m) => m !== previous.midi)
      : group.midis
  return toNote(pick(candidates), group.clef)
}

export function generateNotes(
  count: number,
  tier: number,
  clefPreference: ClefPreference,
): GeneratedNote[] {
  const pool = notePoolForTier(tier, clefPreference)
  const notes: GeneratedNote[] = []
  for (let i = 0; i < count; i++) {
    notes.push(generateNote(pool, notes[notes.length - 1]))
  }
  return notes
}
